import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { GenericDialog } from '@AMIEWEB/Alerts/GenericDialog';
import { Cancel } from '@AMIEWEB/Common/CancelModal/Cancel';
import { Undo } from '@AMIEWEB/Common/UndoModal/Undo';
import { useDispatch, useSelector } from 'react-redux';
import { selectFacility } from '@AMIEWEB/Facility/FacilityStore/Facility.selector';
import { SimpleCheckbox } from '@AMIEWEB/Unit/Common/SimpleCheckbox';
import { SingleLineInput } from '@AMIEWEB/Unit/Common/SingleLineInput';
import { facilityActions } from '@AMIEWEB/Facility/FacilityStore/Facility.redux';
import { IUpdateReferencesCommand } from '@AMIEWEB/Facility/FacilityStore/Facility.model';
import { selectUser } from 'oidc/user.selectors';
import { userRoles } from 'oidc/userRoles';
import { Authorized } from 'oidc/userHelper';
import { CustomTooltip } from '@AMIEWEB/Common/Tooltips';
import { usePromiseTracker } from 'react-promise-tracker';
import { updateFacilityReferencePromiseKey } from '@AMIEWEB/Facility/FacilityStore/Facility.saga';
import { isWholeNumber } from '@AMIEWEB/Order/OrderCreationForm/helper';
import { useEditReferencesStyles } from './styles';

export const EditReferences = props => {
  const { t } = useTranslation();
  const { classes } = useEditReferencesStyles();
  const dispatch = useDispatch();
  const { isPopupOpen, setPopupOpen } = props;
  const facility = useSelector(selectFacility);
  const { userInfo } = useSelector(selectUser);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [undoDialogOpen, setUndoDialogOpen] = useState<boolean>(false);
  const facilityDetailsResponse = facility?.facilityResponse;
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const { promiseInProgress: isBusyUpdating } = usePromiseTracker({
    area: updateFacilityReferencePromiseKey,
    delay: 0,
  });
  const [updatingReferences, setUpdatingReferences] = useState<boolean>(false);

  const {
    numberOfReferenceRequired,
    numberOfReferenceToRFO,
    submissionRequiredReference,
    supervisorAndAboveReference,
    withinLastMonthsReferenceChanged,
    entireWorkHistory,
  } = facilityDetailsResponse;

  const {
    handleSubmit,
    formState: { isDirty },
    reset,
    control,
    setValue,
  } = useForm({
    defaultValues: {
      numberOfReferenceRequired: numberOfReferenceRequired ?? null,
      numberOfReferencesToRFO: numberOfReferenceToRFO ?? null,
      submissionRequiredReference: submissionRequiredReference ?? false,
      supervisorAndAboveReference: supervisorAndAboveReference ?? false,
      withinLastMonthsReferenceChanged: withinLastMonthsReferenceChanged ?? null,
      entireWorkHistory: entireWorkHistory ?? false,
    },
  });
  const [referancesToStartFocused, setReferancesToStartFocused] = useState<boolean>();
  const [referancesToRFOFocused, setReferancesToRFOFocused] = useState<boolean>();
  const [entireWorkHistoryFocused, setEntireWorkHistoryFocused] = useState<boolean>(false);

  const formValues = useWatch({
    control,
    name: [
      'entireWorkHistory',
      'numberOfReferencesToRFO',
      'numberOfReferenceRequired',
      'withinLastMonthsReferenceChanged',
    ],
  });

  const [
    watchedEntireWorkHistory,
    watchedNumberOfReferencesToRFO,
    watchedNumberOfReferenceRequired,
    watchedWithinLastMonthsReferenceChanged,
  ] = formValues;

  useEffect(() => {
    const isRequired = watchedNumberOfReferencesToRFO > 0;
    setValue('submissionRequiredReference', isRequired);
  }, [watchedNumberOfReferencesToRFO]);

  useEffect(() => {
    watchedEntireWorkHistory
      ? setEntireWorkHistoryFocused(false)
      : watchedWithinLastMonthsReferenceChanged == null
      ? setEntireWorkHistoryFocused(false)
      : setEntireWorkHistoryFocused(true);
  }, [watchedEntireWorkHistory]);

  const isAuthorized = Authorized([userRoles.clinical_ClientContractSpecialist], userInfo);

  const onSubmit = data => {
    const updatedReferences: IUpdateReferencesCommand = {
      ...data,
      numberOfReferenceRequired: isNaN(parseInt(data?.numberOfReferenceRequired))
        ? 0
        : parseInt(data?.numberOfReferenceRequired),
      numberOfReferencesToRFO: isNaN(parseInt(data?.numberOfReferencesToRFO))
        ? 0
        : parseInt(data?.numberOfReferencesToRFO),
      withinLastMonthsReferenceChanged: isNaN(parseInt(data?.withinLastMonthsReferenceChanged))
        ? null
        : parseInt(data?.withinLastMonthsReferenceChanged),
      facilityId: facilityDetailsResponse?.facilityId,
      currentEmployeeId: userInfo.employeeId,
      currentEmployeeName: `${userInfo?.firstName} ${userInfo?.lastName}`,
    };
    dispatch(facilityActions.updateReferencesAction(updatedReferences));
    setUpdatingReferences(true);
  };

  useEffect(() => {
    if (!isBusyUpdating && updatingReferences) {
      setPopupOpen(false);
      setUpdatingReferences(false);
    }
  }, [isBusyUpdating, setPopupOpen, isPopupOpen]);

  const onDiscardChanges = () => {
    setConfirmDialogOpen(false);
    setPopupOpen(false);
  };

  const onClose = () => {
    if (isDirty) setConfirmDialogOpen(true);
    else setPopupOpen(false);
  };
  let showTimeout;
  let hideTimeout;

  const handleMouseEnter = () => {
    showTimeout = setTimeout(() => {
      setIsHovered(true);
    }, 200);
  };

  const handleMouseLeave = () => {
    clearTimeout(showTimeout);
    hideTimeout = setTimeout(() => {
      setIsHovered(false);
    }, 200);
  };

  useEffect(() => {
    return () => {
      clearTimeout(showTimeout);
      clearTimeout(hideTimeout);
    };
  }, [showTimeout, hideTimeout]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <GenericDialog
          fullWidth
          variant={'blue'}
          classes={{
            paper: classes.dialogContainer,
          }}
          open={isPopupOpen}
          disablePortal
          disableEscapeKeyDown
          dialogTitleProps={{
            text: t('facility.editReferences'),
            closeButton: true,
            expandable: true,
            undoButton: true,
            onUndoClick: () => setUndoDialogOpen(true),
            disableUndo: !isDirty,
          }}
          dialogActions={[
            {
              text: t('global.button.cancel'),
              variant: 'contained',
              color: 'tertiary',
              disabled: isBusyUpdating,
              onClick: onClose,
            },
            {
              text: t('global.button.save'),
              variant: 'contained',
              color: 'primary',
              type: 'submit',
              disabled: !isDirty || isBusyUpdating,
              loading: isBusyUpdating,
            },
          ]}
          dialogContentProps={{
            classes: { root: classes.dialogContent },
          }}
          onClose={onClose}
        >
          <Grid container rowSpacing={2} columnSpacing={2}>
            <Grid item xs={4} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
              <CustomTooltip
                open={!isAuthorized && isHovered}
                placement="bottom"
                arrow={true}
                tooltipContent={t('facility.references.tooltipText')}
              >
                <Controller
                  name={'numberOfReferenceRequired'}
                  control={control}
                  render={({ field }) => (
                    <SingleLineInput
                      id={'numberOfReferenceRequired'}
                      inputDefaultValue={field.value}
                      inputLabel={t('facility.references.referencesRequired')}
                      inputName={'numberOfReferenceRequired'}
                      inputValue={field.value}
                      handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        field.onChange(parseInt(event.target.value));
                      }}
                      handleWarning={() => setReferancesToStartFocused(true)}
                      handleBlur={() => {
                        setReferancesToStartFocused(false);
                        field.onBlur();
                      }}
                      shrinkLabel={referancesToStartFocused || isWholeNumber(watchedNumberOfReferenceRequired)}
                      inputType={'number'}
                      fieldStyle={classes.fieldStyle}
                      isdisabled={!isAuthorized}
                    />
                  )}
                />
              </CustomTooltip>
            </Grid>
            <Grid item xs={4}>
              <Controller
                name={'numberOfReferencesToRFO'}
                control={control}
                render={({ field }) => (
                  <SingleLineInput
                    id={'numberOfReferenceToRFO'}
                    inputDefaultValue={field.value}
                    inputLabel={t('facility.references.referenceToSubmit')}
                    inputName={'numberOfReferenceToRFO'}
                    inputValue={field.value}
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(parseInt(event.target.value));
                    }}
                    shrinkLabel={referancesToRFOFocused || isWholeNumber(watchedNumberOfReferencesToRFO)}
                    handleWarning={() => setReferancesToRFOFocused(true)}
                    handleBlur={() => {
                      setReferancesToRFOFocused(false);
                      field.onBlur();
                    }}
                    inputType={'number'}
                    fieldStyle={classes.fieldStyle}
                  />
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <Controller
                control={control}
                name={'submissionRequiredReference'}
                render={({ field }) => (
                  <SimpleCheckbox
                    id={'submissionRequired'}
                    isChecked={field.value}
                    handleChecked={(event: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(event.target.checked);
                    }}
                    isdisabled={true}
                    checkboxLabel={t('facility.references.submissionRequrement')}
                    checkboxName={'submission-required-reference'}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name={'withinLastMonthsReferenceChanged'}
                control={control}
                render={({ field }) => (
                  <SingleLineInput
                    id={'withinLastMonthsReferenceChanged'}
                    inputDefaultValue={field.value}
                    inputLabel={t('facility.references.withinLastMonth')}
                    inputName={'withinLastMonthsReferenceChanged'}
                    inputValue={watchedEntireWorkHistory ? '' : field.value ?? ''}
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(parseInt(event.target.value));
                    }}
                    shrinkLabel={
                      entireWorkHistoryFocused ||
                      (isWholeNumber(watchedWithinLastMonthsReferenceChanged) && !watchedEntireWorkHistory)
                    }
                    handleWarning={() => setEntireWorkHistoryFocused(true)}
                    handleBlur={() => {
                      setEntireWorkHistoryFocused(false);
                      field.onBlur();
                    }}
                    inputType={'number'}
                    fieldStyle={!watchedEntireWorkHistory ? classes.fieldStyle : classes.fieldStyleNonHover}
                    showEndAdornment={
                      entireWorkHistoryFocused ||
                      (isWholeNumber(watchedWithinLastMonthsReferenceChanged) && !watchedEntireWorkHistory)
                    }
                    endAdornmentValue={t('facility.references.months')}
                    isdisabled={watchedEntireWorkHistory}
                  />
                )}
              />
            </Grid>

            <Grid item xs={4} mt={'10px'}>
              <Controller
                control={control}
                name={'entireWorkHistory'}
                render={({ field }) => (
                  <SimpleCheckbox
                    id={'entireWorkHistory'}
                    isChecked={field.value}
                    handleChecked={(event: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(event.target.checked);
                      if (event.target.checked) setValue('withinLastMonthsReferenceChanged', null);
                    }}
                    isdisabled={false}
                    checkboxLabel={t('facility.references.entireWorkHistory')}
                    checkboxName={'entire-work-history-reference'}
                  />
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <Controller
                control={control}
                name={'supervisorAndAboveReference'}
                render={({ field }) => (
                  <SimpleCheckbox
                    id={'supervisorAndAbove'}
                    isChecked={field.value}
                    handleChecked={(event: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(event.target.checked);
                    }}
                    isdisabled={false}
                    checkboxLabel={t('facility.references.supervisorAndAbove')}
                    checkboxName={'supervisor-and-above-Reference'}
                  />
                )}
              />
            </Grid>
          </Grid>
        </GenericDialog>
      </form>
      {confirmDialogOpen && (
        <Cancel
          openDialog={confirmDialogOpen}
          handleCancelAction={() => {
            setConfirmDialogOpen(false);
          }}
          handleConfirmAction={onDiscardChanges}
          cancelButtonId={'facility-references-discard-changes-cancel-button'}
          confirmButtonId={'facility-references-discard-changes-confirm-button'}
        />
      )}
      {undoDialogOpen && (
        <Undo
          openDialog={undoDialogOpen}
          handleConfirmAction={() => {
            reset();
            setUndoDialogOpen(false);
          }}
          handleCancelAction={() => {
            setUndoDialogOpen(false);
          }}
        />
      )}
    </>
  );
};
